import { distance } from '@turf/distance'
import { SimpleCoordinates, RouteProfile } from './types'
import { LngLatBounds, LngLatBoundsLike } from 'mapbox-gl'
import { CollectionItem, MediaItem } from 'mytours-read-common'

import { getCurrentLanguage } from '../platform'
import { generateMapPinIconUrl } from 'common_lib/maps/functions'
import { MapItem } from 'mytours-read-api-typescript'
import mapboxgl from 'mapbox-gl'
import { ImageSizePreset } from 'common_lib/types'
import { generateImageUrl, passZerosBeforeNumber } from 'common_lib/utils'

export enum SwitchDirection {
	Next = 'next',
	Prev = 'prev',
}

interface SwitchItemResponse {
	newItem: CollectionItem
	newItemIndex: number
	pageIndex: number
}

const getMinOrMax = (coords: SimpleCoordinates[], minOrMax: 'min' | 'max', latOrLng: 'lat' | 'lng'): number => {
	const markersArr: number[] = coords.map((coord: SimpleCoordinates) => Number(coord[latOrLng]))

	if (minOrMax == 'max') {
		return Math.max(...markersArr)
	} else {
		return Math.min(...markersArr)
	}
}

export const getBounds = (coords: SimpleCoordinates[]): LngLatBoundsLike => {
	const maxLat = getMinOrMax(coords, 'max', 'lat')
	const minLat = getMinOrMax(coords, 'min', 'lat')
	const maxLng = getMinOrMax(coords, 'max', 'lng')
	const minLng = getMinOrMax(coords, 'min', 'lng')

	const southWest = [minLng, minLat]
	const northEast = [maxLng, maxLat]
	return [southWest, northEast] as LngLatBoundsLike
}

const getItemIndex = (markers: CollectionItem[], itemId: number, direction: SwitchDirection): number => {
	const itemIndex = markers.findIndex(item => item.id == itemId)

	let newItemIndex: number

	switch (direction) {
		case SwitchDirection.Prev:
			if (markers[itemIndex - 1]) {
				newItemIndex = itemIndex - 1
			} else if (markers[markers.length - 1]) {
				newItemIndex = markers.length - 1
			}
			break
		case SwitchDirection.Next:
			if (markers[itemIndex + 1]) {
				newItemIndex = itemIndex + 1
			} else if (markers[0]) {
				newItemIndex = 0
			}
			break
	}

	return newItemIndex
}

export const switchItemAction = (
	itemId: number,
	direction: SwitchDirection,
	items: CollectionItem[],
): SwitchItemResponse => {
	const markers = items.filter(item => item && item.lat && item.lng)

	const newItemIndex = getItemIndex(markers, itemId, direction)
	const newItem = items.find(item => item.id == markers[newItemIndex].id)
	const pageIndex: number = items && items.findIndex(item => item.id === newItem.id)

	return { newItem, newItemIndex, pageIndex }
}

export const defaultMapStyle = 'mytoursapp/cj8arl41r6n6u2rqipa68cgwg'

export function generateImageMapUrl(
	mapStyle: string,
	lat: number,
	lng: number,
	size?: string,
	resizePin?: number,
	icon: string = 'default',
	style: string = 'default',
	color: string = 'default',
): string {
	const mapToken = ENV.MAP_API_ACCESS_TOKEN
	const markerURI = encodeURIComponent(generateMapPinIconUrl({ padding: true, style, color, icon, resize: resizePin }))
	if (!size) size = '1000x1000'
	return `https://api.mapbox.com/styles/v1/${mapStyle}/static/url-${markerURI}(${lng},${lat})/${lng},${lat},18,0.00,0.00/${size}?access_token=${mapToken}&logo=false`
}

export function generateBoundsImageMapUrl(markers: MapItem[], mapStyle: string, size = '1000x1000'): string {
	const mapToken = ENV.MAP_API_ACCESS_TOKEN

	const bounds = new LngLatBounds()
	markers.forEach(m => bounds.extend(new mapboxgl.LngLat(+m.lng, +m.lat)))

	const sw = bounds.getSouthWest()
	const ne = bounds.getNorthEast()

	const boundingBox = sw && ne ? `[${sw.lng},${sw.lat},${ne.lng},${ne.lat}]` : '[-175,-85,175,85]'

	return `https://api.mapbox.com/styles/v1/${mapStyle}/static/${boundingBox}/${size}?${
		sw && ne ? 'padding=50&' : ''
	}access_token=${mapToken}`
}

export const getDirectionRoute = (routeProfile: RouteProfile, startCoords: string, endCoords: string): Promise<any> =>
	new Promise((resolve, reject) => {
		const language = getCurrentLanguage()

		const baseAPIUrl = 'https://api.mapbox.com/directions/v5/mapbox'
		const urlGetParams = `?steps=true&geometries=geojson&access_token=${ENV.MAP_API_ACCESS_TOKEN}&language=${language}`
		const url = `${baseAPIUrl}/${routeProfile}/${startCoords};${endCoords}${urlGetParams}`

		const req = new XMLHttpRequest()
		req.open('GET', url, true)
		req.onload = () => {
			try {
				const json = JSON.parse(req.response)
				const data = json.routes[0]

				resolve(data)
			} catch (error) {
				reject(error)
			}
		}
		req.onerror = error => reject(error)
		req.send()
	})

export const getCurrentPosition = (): Promise<SimpleCoordinates> =>
	new Promise((resolve, reject) => {
		if ('geolocation' in navigator) {
			navigator.geolocation.getCurrentPosition(async ({ coords: { latitude, longitude } }) => {
				const coordinates = {
					lng: longitude,
					lat: latitude,
				}
				resolve(coordinates)
			})
		} else {
			reject('can not get coordinates')
		}
	})

let removeFullWidthClassBlocker = false

export const removeFullWidthFromTabs = (): void => {
	document.querySelector('.home-tabs')?.classList?.remove('full-width')
	removeFullWidthClassBlocker = true
	setTimeout(() => (removeFullWidthClassBlocker = false), 1000)
}

export const checkAndremoveFullWidthClass = (): void => {
	// the next fix to return back page width after maps view

	// use interval to get the end of animation
	// it's better than setTimeout because we really know when conditions match
	// the page is animating until the ion-page-invisible class went off
	const waitForIonPage = setInterval(() => {
		const ionPagesInsideTabs = Array.from(document.querySelectorAll('.home-tabs ion-router-outlet > .ion-page'))
		// check if some page has class `ion-page-invisible`
		const hasIonPageInvisible = ionPagesInsideTabs.some(pageNode => pageNode.classList.contains('ion-page-invisible'))

		// if no, it means animation finished and pages have class `ion-page-hidden` or just `ion-page`
		if (!hasIonPageInvisible) {
			clearInterval(waitForIonPage)

			// get all pages inside tabs router
			const ionPagesInsideTabs = document.querySelectorAll('.home-tabs ion-router-outlet > .ion-page')

			let isFullWidth = false
			ionPagesInsideTabs.forEach(page => {
				// check if a page has map view
				if (page.classList.contains('full-width') && !page.classList.contains('ion-page-hidden')) {
					isFullWidth = true
					return
				}
			})

			// use `full-width` for `home-tabs` to set the tabs in full screen
			const homeTabsClassList = document.querySelector('.home-tabs')?.classList

			if (isFullWidth) {
				// use `removeFullWidthClassBlocker` to prevent adding class right after removing
				if (!removeFullWidthClassBlocker) homeTabsClassList?.add('full-width')
			} else {
				homeTabsClassList?.remove('full-width')
			}
		}
	}, 10)
}

interface FormatMapPinIcon {
	mapPinIcon: string
	itemNumber: number
	keypadEnabled: boolean
	keypadDigits: number
}
export const formatMapPinIcon = ({ mapPinIcon, itemNumber, keypadEnabled, keypadDigits }: FormatMapPinIcon): string => {
	if (mapPinIcon !== 'default') {
		return mapPinIcon
	}

	if (itemNumber) {
		return keypadEnabled ? passZerosBeforeNumber(keypadDigits, itemNumber) : String(itemNumber)
	}

	return mapPinIcon
}

export const buildPointMapPinPopup = (feat: any, mediaItem: MediaItem): HTMLDivElement => {
	const {
		properties: { title, description },
	} = feat
	const wrapEl = document.createElement('div')
	wrapEl.className = 'point-popup__wrap'

	const popup = document.createElement('div')
	popup.className = 'point-popup'

	const titleEl = document.createElement('h5')
	titleEl.innerText = title

	const descrEl = document.createElement('div')
	descrEl.innerHTML = typeof description === 'string' ? description : ''

	const textWrapEl = document.createElement('div')
	textWrapEl.className = 'point-popup__details'
	textWrapEl.appendChild(titleEl)
	textWrapEl.appendChild(descrEl)

	if (mediaItem?.file) {
		const imgWrapEl = document.createElement('div')
		imgWrapEl.className = 'point-popup__img'
		const imgUrl = generateImageUrl(mediaItem.file, {
			imageSizePreset: ImageSizePreset.Grid,
		})
		imgWrapEl.style.backgroundImage = `url(${imgUrl})`
		popup.appendChild(imgWrapEl)
	}

	popup.appendChild(textWrapEl)

	wrapEl.appendChild(popup)
	const container = document.createElement('div')
	container.appendChild(wrapEl)

	return container
}

export const findClosestPointIndex = (points: number[][], target: number[]) => {
	let position = 0
	let closestDist = distance(points[0], target)
	points.forEach((point, i) => {
		const dist = distance(point, target)
		if (dist < closestDist) {
			closestDist = dist
			position = i
		}
	})
	return position
}
